import {
  ImageRefDto,
  LogoDto,
  ProductRefDto,
  TemporaryColorChangeDto,
} from "../responses/LogoDto";
import {
  ApprovalType,
  BaseLogo,
  ImageLogo,
  ImageRef,
  LogoType,
  NameLogo,
  NumberLogo,
  ProductPrice,
  ProductRef,
  SymbolLogo,
  TemporaryColorChange,
  TextLogo,
} from "../../../core/models/entities/BaseLogo";
import { mapToOrganization } from "./mapOrganization";
import { TransferMethod } from "../../../core/models/entities/LogoOptions";
import { mapToApprovalGroup } from "./ApprovalMapper";
import { mapToAdditionalFee } from "./mapOrder";
import { PriceDto } from "../responses/PriceDto";
import { TransferMethodDto } from "../responses/logoOptionsDto";

// The list/thumbnail images are stored blobs that get OVERWRITTEN in place on
// color changes (same URL, new bytes) — the browser then keeps serving its cached
// old bytes. Version the URL with a token that changes exactly when the image
// content changes, so unchanged logos stay cacheable but mutations refetch.
function appendVersion(
  url: string | undefined,
  version: number
): string | undefined {
  if (!url || url.startsWith("data:") || !version) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${version}`;
}

function mapToImageRef(dto: ImageRefDto, version = 0): ImageRef {
  const lowResUrl = appendVersion(dto.lowResUrl, version) as string;
  return {
    ...dto,
    lowResUrl,
    digitUrl: appendVersion(dto.digitUrl, version) as string,
    trueColorsUrl: appendVersion(dto.trueColorsUrl, version),
    highResUrl: dto.highResUrl ?? lowResUrl,
    // ADDITIVE: expose the cold state the fallback above otherwise hides —
    // true means "no genuine high-res yet, highResUrl is really lowResUrl".
    // The fallback itself is deliberately untouched (existing consumers
    // depend on highResUrl always being a usable URL).
    highResPending: !dto.highResUrl,
  };
}

function mapToLogoType(logoType: string): LogoType {
  return LogoType[logoType as keyof typeof LogoType] ?? LogoType.Unknown;
}

function mapToProductPrice(dto: PriceDto): ProductPrice {
  return { ...dto };
}

export function mapToProductRef(dto: ProductRefDto): ProductRef {
  return {
    ...dto,
    additionalFees: dto.additionalFees?.map(mapToAdditionalFee) ?? [],
    // Null-guard (same class of bug as mapMultiProductRef): prices can be
    // missing while the NAV price lookup hasn't settled yet — a .map() on it
    // would throw inside react-query's select and flip the editor to the
    // error view even though the server responded 200.
    prices: dto.prices ? dto.prices.map((p) => mapToProductPrice(p)) : [],
  };
}

function mapToTransferMethod(dto: TransferMethodDto): TransferMethod {
  return { ...dto };
}

function mapToBaseLogo(dto: LogoDto): BaseLogo {
  const mappedApproval = dto.approval
    ? mapToApprovalGroup(dto.approval)
    : undefined;

  // Only map organization if it exists
  const customerOrg = dto.customer || dto.vendor;
  const mappedCustomer = customerOrg
    ? mapToOrganization(customerOrg)
    : undefined;

  // Use the approval type determined by the backend
  const approvalType = dto.approvalType;

  // Image version token: the backend's createdAtUtc carries LastWriteTime for logos,
  // and colorChange.startedAtUtc is set on every color change — the max of the two
  // changes exactly when the underlying image blob is rewritten. (UpdatedAtUtc is
  // insert-only for logos and never changes, so it cannot be used here.)
  const imageVersion = Math.max(
    Date.parse(dto.createdAtUtc) || 0,
    dto.colorChange?.startedAtUtc
      ? Date.parse(dto.colorChange.startedAtUtc) || 0
      : 0
  );

  return {
    ...dto,
    // Recovered/awaiting-image logos can carry a null displayImage until the
    // background worker has generated it — mapping it unguarded would throw.
    displayImage: dto.displayImage
      ? mapToImageRef(dto.displayImage, imageVersion)
      : (undefined as any),
    customer: mappedCustomer,
    customerId: dto.customer?.id,
    createdAtUtc: new Date(dto.createdAtUtc),
    updateAtUtc: dto.updatedAtUtc ? new Date(dto.updatedAtUtc) : undefined, // Fixed: map from updatedAtUtc to updateAtUtc
    deletedAtUtc: dto.deletedAtUtc ? new Date(dto.deletedAtUtc) : undefined,
    product: dto.product ? mapToProductRef(dto.product) : null,
    transferMethod: mapToTransferMethod(dto.transferMethod),
    approval: mappedApproval,
    approvalType: approvalType,
    inStorage: dto.inStorage,
    numberLogosInStorage: dto.numberLogosInStorage,
    storageUpdatedAtUtc: dto.storageUpdatedAtUtc ?? null,
    designCount: dto.designCount,
    designerWidthMm: dto.designerWidthMm,
    colors: dto.colors,
    colorDetails: dto.colorDetails,
    mainOrganizationId: dto.mainOrganizationId,
    Classification: (dto as any).Classification ?? (dto as any).classification ?? undefined,
  } as BaseLogo;
}

function mapToNameLogo(dto: LogoDto): NameLogo {
  return {
    ...mapToBaseLogo(dto),
    font: dto.font,
    logoType: LogoType.NameLogo,
  };
}

function mapToNumberLogo(dto: LogoDto): NumberLogo {
  return {
    ...mapToBaseLogo(dto),
    font: dto.font,
    logoType: LogoType.NumberLogo,
  };
}

function mapToTemporaryColorChange(
  dto: TemporaryColorChangeDto
): TemporaryColorChange {
  return { ...dto, startedAtUtc: new Date(dto.startedAtUtc) };
}

function mapToImageLogo(dto: LogoDto): ImageLogo {
  return {
    ...mapToBaseLogo(dto),
    isDigital: dto.isDigital,
    isChangeColor: dto.isChangeColor,
    status: dto.status,
    colorChange: dto.colorChange
      ? mapToTemporaryColorChange(dto.colorChange)
      : undefined,
    colors: dto.colors,
    approval: dto.approval ? mapToApprovalGroup(dto.approval) : undefined,
    originalFileName: dto.originalFileName,
    logoType: LogoType.ImageLogo,
  };
}

function mapToSymbolLogo(dto: LogoDto): SymbolLogo {
  return {
    ...mapToBaseLogo(dto),
    symbolKey: dto.symbolKey,
    color: dto.color,
    logoType: LogoType.SymbolLogo,
  };
}

function mapToTextLogo(dto: LogoDto): TextLogo {
  return {
    ...mapToBaseLogo(dto),
    value: dto.value,
    font: dto.font,
    logoType: LogoType.TextLogo,
  };
}

export function mapToLogo(dto: LogoDto): BaseLogo {
  const logotype = mapToLogoType(dto.logoType);

  switch (logotype) {
    default:
    case LogoType.Unknown:
      console.warn("unknown logo type:", logotype);
      return mapToBaseLogo(dto);
    case LogoType.TextLogo:
      return mapToTextLogo(dto);
    case LogoType.NameLogo:
      return mapToNameLogo(dto);
    case LogoType.NumberLogo:
      return mapToNumberLogo(dto);
    case LogoType.ImageLogo:
      return mapToImageLogo(dto);
    case LogoType.SymbolLogo:
      return mapToSymbolLogo(dto);
  }
}
